import { Injectable, inject } from '@angular/core';
import { LookupItem, LookupService } from './LookupService';
import { TranslationService } from './TranslationService';
import { decalageMinutes } from '../helpers/dateHelpers';

// Le fuseau des sites, signale au lecteur seulement quand son decalage differe du sien.
@Injectable({ providedIn: 'root' })
export class SiteTimeZoneService {
  private readonly lookupService = inject(LookupService);

  private _table: Map<number, string> | null = null;
  private _source: [LookupItem[] | undefined, LookupItem[] | undefined] = [undefined, undefined];

  // Table `id de site -> IANA`. Instance STABLE tant que les lookups n'ont pas change.
  fuseauxParSite(): Map<number, string> {
    const sites = this.lookupService.getLookupData('PointOfService');
    const fuseaux = this.lookupService.getLookupData('TimeZone');

    if (this._table && this.memeLookups(sites, fuseaux)) return this._table;

    const table = new Map<number, string>();
    const maintenant = new Date();
    const celuiDuLecteur = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const decalageLecteur = decalageMinutes(celuiDuLecteur, maintenant);

    // Un meme fuseau est partage par plusieurs sites : on ne resout son IANA qu'une fois.
    const ianaParFuseau = new Map<string, string | null>();
    const ianaDe = (cleFuseau: string): string | null => {
      if (!ianaParFuseau.has(cleFuseau)) {
        const iana = fuseaux.find(item => `${item.key}` === cleFuseau)?.additionalData?.['ianaid'];
        ianaParFuseau.set(cleFuseau, iana == null || `${iana}` === '' ? null : `${iana}`);
      }
      return ianaParFuseau.get(cleFuseau)!;
    };

    for (const site of sites) {
      const cleFuseau = site.additionalData?.['timezone'];
      if (cleFuseau == null || `${cleFuseau}` === '') continue;

      const id = Number(site.key);
      if (!Number.isFinite(id)) continue;

      const iana = ianaDe(`${cleFuseau}`);
      if (!iana) continue;

      if (iana === celuiDuLecteur) continue;
      const decalageSite = decalageMinutes(iana, maintenant);
      if (decalageSite != null && decalageLecteur != null && decalageSite === decalageLecteur) continue;

      table.set(id, iana);
    }

    this._source = [sites, fuseaux];
    this._table = table;
    return table;
  }

  /** Fuseau d'UN site, ou null si son decalage est celui du lecteur. */
  fuseauDuSite(fkPointOfService: number | string | null | undefined): string | null {
    if (fkPointOfService == null || `${fkPointOfService}` === '') return null;
    const id = Number(fkPointOfService);
    if (!Number.isFinite(id)) return null;
    return this.fuseauxParSite().get(id) ?? null;
  }

  // Ecart en heures site/lecteur ; le texte de l'infobulle vit dans `TimeZoneIconComponent`.
  ecartHoraire(iana: string): string {
    if (!iana) return '';

    const maintenant = new Date();
    const site = decalageMinutes(iana, maintenant);
    const lecteur = decalageMinutes(Intl.DateTimeFormat().resolvedOptions().timeZone, maintenant);
    if (site == null || lecteur == null) return '';

    const minutes = site - lecteur;
    const signe = minutes >= 0 ? '+' : '-';
    const absolu = Math.abs(minutes);
    const heures = Math.floor(absolu / 60);
    const reste = absolu % 60;
    return `${signe}${heures} h${reste ? ' ' + reste.toString().padStart(2, '0') : ''}`;
  }

  // `getLookupData` rend un tableau NEUF si absent : deux tableaux vides sont « les memes ».
  private memeLookups(sites: LookupItem[], fuseaux: LookupItem[]): boolean {
    const identique = (precedent: LookupItem[] | undefined, courant: LookupItem[]) =>
      precedent === courant || (precedent != null && precedent.length === 0 && courant.length === 0);
    return identique(this._source[0], sites) && identique(this._source[1], fuseaux);
  }
}
